import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /**
   * Accepts a list because the API answers a rejected password with every
   * broken rule at once; collapsing that to `messages[0]` forces the user
   * through one round trip per rule.
   */
  error?: string | string[] | undefined;
  hint?: string | undefined;
  endAdornment?: ReactNode | undefined;
}

/**
 * Label, hint and error are wired to the input by id, so screen readers
 * announce the error rather than leaving a field that is merely red (RNF-07).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, endAdornment, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  // An empty array is truthy, so a bare `error &&` would paint a red border
  // with nothing under it and read as "invalid" to `aria-invalid`.
  const messages = error === undefined ? [] : Array.isArray(error) ? error : [error];
  const hasError = messages.length > 0;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-ink text-sm font-medium">
        {label}
      </label>

      {endAdornment ? (
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            aria-invalid={hasError || undefined}
            aria-describedby={cn(hint && hintId, hasError && errorId) || undefined}
            className={cn(
              'border-border bg-surface h-11 w-full rounded-(--radius-control) border px-3 pr-10 text-sm',
              'focus:border-brand-900 focus:outline-none',
              'disabled:bg-surface-sunken disabled:text-ink-subtle',
              hasError && 'border-danger',
              className,
            )}
            {...rest}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{endAdornment}</div>
        </div>
      ) : (
        <input
          ref={ref}
          id={inputId}
          aria-invalid={hasError || undefined}
          aria-describedby={cn(hint && hintId, hasError && errorId) || undefined}
          className={cn(
            'border-border bg-surface h-11 rounded-(--radius-control) border px-3 text-sm',
            'focus:border-brand-900 focus:outline-none',
            'disabled:bg-surface-sunken disabled:text-ink-subtle',
            hasError && 'border-danger',
            className,
          )}
          {...rest}
        />
      )}

      {hint && !hasError && (
        <p id={hintId} className="text-ink-subtle text-xs">
          {hint}
        </p>
      )}
      {hasError &&
        (messages.length === 1 ? (
          <p id={errorId} role="alert" className="text-danger text-xs">
            {messages[0]}
          </p>
        ) : (
          <ul
            id={errorId}
            role="alert"
            className="text-danger flex list-disc flex-col gap-0.5 pl-4 text-xs"
          >
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ))}
    </div>
  );
});
